import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import starIcon from '../../assets/dashboard_assets/star.png';
import PageTitle from './components/PageTitle';

const TYPE_FILTERS = [
    { key: '', label: 'All' },
    { key: 'listing', label: 'Listings' },
    { key: 'listing_score', label: 'Listing Score' },
    { key: 'deal_tag', label: 'Deal Tag' },
    { key: 'catalogue_fix', label: 'Catalogue Fixes' },
];

const TYPE_BADGE = {
    listing: 'bg-[#D0E7FF]/40 text-[#2766AA]',
    listing_score: 'bg-[#FFF4E5] text-[#B36B00]',
    deal_tag: 'bg-[#F0EBFF] text-[#5B3FB8]',
    catalogue_fix: 'bg-[#E8F7ED] text-[#2E9E5C]',
};

const formatDate = (iso) => {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso;
        return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
            + ' · ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    } catch {
        return iso;
    }
};

const UsageHistoryPage = () => {
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [perPage] = useState(20);
    const [total, setTotal] = useState(0);
    const [type, setType] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

    useEffect(() => {
        if (!authCtx.token) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        const qs = new URLSearchParams({ page: String(page), per_page: String(perPage) });
        if (type) qs.set('type', type);
        (async () => {
            try {
                const res = await fetch(`${API_URL}/auth/usage-history?${qs.toString()}`, {
                    headers: { Authorization: `Bearer ${authCtx.token}` },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (cancelled) return;
                setItems(data.items || []);
                setTotal(data.total || 0);
            } catch (e) {
                if (!cancelled) setError(e.message || 'Failed to load history');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [authCtx.token, API_URL, page, perPage, type]);

    const lastPage = Math.max(1, Math.ceil(total / perPage));
    const totalCreditsPage = items.reduce((s, it) => s + (it.credits || 0), 0);

    return (
        <div className="w-full max-w-[1550px] mx-auto bg-white shadow-[0_0_134px_14px_rgba(120,179,243,0.07)] rounded-2xl overflow-hidden p-6 pb-16 mb-16 origin-top" style={{ transform: 'scale(0.9)' }}>
            {/* Back link */}
            <button
                onClick={() => navigate('/dashboard/plans')}
                className="text-[#2766AA] text-sm font-semibold hover:underline bg-transparent border-none p-0 mb-2 cursor-pointer"
            >
                ← Back to Buy Credits
            </button>

            <PageTitle title="Usage history" badge="Every action you've taken on EcomBuddha and what it cost." />

            {/* Filter chips + page credits summary */}
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div className="flex flex-wrap gap-2">
                    {TYPE_FILTERS.map(f => (
                        <button
                            key={f.key || 'all'}
                            onClick={() => { setType(f.key); setPage(1); }}
                            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                                type === f.key
                                    ? 'bg-gradient-to-r from-[#2766AA] to-[#0752B0] text-white'
                                    : 'bg-white text-[#505050] hover:bg-[#F8FBFF] border border-[#D0E7FF]'
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/80 rounded-full outline outline-1 outline-[#D0E7FF]">
                    <img src={starIcon} alt="" className="w-4 h-4" />
                    <span className="text-[#737373] text-xs font-semibold uppercase tracking-wide">On this page:</span>
                    <span className="text-[#3A3A3A] text-sm font-bold">{totalCreditsPage.toLocaleString()} credits</span>
                </div>
            </div>

            {/* List card */}
            <div className="w-full bg-white/80 shadow-[0_0_134px_14px_rgba(120,179,243,0.06)] rounded-3xl outline outline-1 outline-[#D0E7FF] px-5 py-5">
                {loading ? (
                    <div className="py-16 text-center text-[#898989] text-sm font-medium">Loading…</div>
                ) : error ? (
                    <div className="py-16 text-center text-red-500 text-sm font-medium">{error}</div>
                ) : items.length === 0 ? (
                    <div className="py-16 text-center text-[#898989] text-sm font-medium">No activity yet.</div>
                ) : (
                    <>
                        {/* Table header */}
                        <div className="grid grid-cols-[160px_1fr_180px_120px] gap-4 px-4 py-2 text-[#737373] text-xs font-semibold uppercase tracking-wide">
                            <span>Type</span>
                            <span>Detail</span>
                            <span>When</span>
                            <span className="text-right">Credits</span>
                        </div>
                        <div className="w-full h-px bg-[#D0E7FF] mb-2" />

                        {/* Rows */}
                        <div className="flex flex-col gap-2">
                            {items.map((it, i) => (
                                <div
                                    key={`${it.resource_id || i}-${i}`}
                                    className="grid grid-cols-[160px_1fr_180px_120px] gap-4 px-4 py-3 bg-white rounded-2xl outline outline-1 outline-[#E8F0FE] items-center hover:outline-[#D0E7FF] hover:bg-[#F8FBFF] transition-colors"
                                >
                                    <span
                                        className={`px-3 py-1 rounded-full text-xs font-semibold w-fit ${TYPE_BADGE[it.type] || 'bg-[#F8FBFF] text-[#505050]'}`}
                                    >
                                        {it.label}
                                    </span>
                                    <span className="text-[#505050] text-sm font-medium truncate" title={it.detail || ''}>
                                        {it.detail || '—'}
                                    </span>
                                    <span className="text-[#737373] text-xs font-medium">
                                        {formatDate(it.timestamp)}
                                    </span>
                                    <div className="flex items-center justify-end gap-1.5">
                                        <img src={starIcon} alt="" className="w-3.5 h-3.5" />
                                        <span className="text-[#3A3A3A] text-sm font-bold">{(it.credits || 0).toLocaleString()}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {/* Pagination */}
                {!loading && total > perPage && (
                    <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#D0E7FF]">
                        <span className="text-[#737373] text-sm font-medium">
                            Page <span className="text-[#3A3A3A] font-semibold">{page}</span> of <span className="text-[#3A3A3A] font-semibold">{lastPage}</span> · {total.toLocaleString()} total
                        </span>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className="px-5 py-2 rounded-lg outline outline-[1.5px] outline-offset-[-1.5px] outline-[#2766AA] text-[#2766AA] text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#2766AA] hover:text-white transition-colors bg-transparent"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setPage(p => Math.min(lastPage, p + 1))}
                                disabled={page >= lastPage}
                                className="px-5 py-2 rounded-lg bg-gradient-to-r from-[#2766AA] to-[#0752B0] text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UsageHistoryPage;
